/* Phase 2 Physiology views — Nutritional, Protein/AA, Oil/FA, Antioxidant, Hormones, Secondary Metabolites.
   Same deep-teal theme; each view re-uses shared Card/Table/Citations from PhysiologyViews.
*/
import React, { useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, LineChart, Line,
  ScatterChart, Scatter,
} from 'recharts';
import DOMPurify from 'dompurify';
import { PHYSIO_REFERENCE_VALUES } from '../physiologySampleData';

// XSS defense (mirrors PhysiologyViews.js): sanitize citation HTML.
const _sanitizeCitation = (html) => DOMPurify.sanitize(html, {
  ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'br'],
  ALLOWED_ATTR: ['href', 'target', 'rel'],
});

const TEAL = '#0F766E';
const TEAL_ACTIVE = '#14B8A6';
const PALETTE = ['#0F766E', '#14B8A6', '#2DD4BF', '#5EEAD4', '#99F6E4', '#0D9488', '#134E4A', '#0369A1', '#B45309', '#991B1B', '#F59E0B', '#7C3AED'];

const fmt = (v, n = 4) => {
  if (v === null || v === undefined || v === '-') return '-';
  if (typeof v === 'number') return v.toFixed(n);
  return String(v);
};

function Card({ title, children, testid }) {
  return (
    <div className="rounded-xl p-4 bg-white" style={{ border: '1px solid #99F6E4' }} data-testid={testid}>
      <h4 className="font-semibold text-sm uppercase tracking-wider mb-2" style={{ color: TEAL }}>{title}</h4>
      {children}
    </div>
  );
}

function Table({ rows, cols }) {
  if (!rows || !rows.length) return <p className="text-xs text-gray-500">No data.</p>;
  const keys = cols || Object.keys(rows[0]);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border-collapse">
        <thead><tr className="bg-teal-100 text-teal-900">
          {keys.map(k => <th key={k} className="px-2 py-1.5 border border-teal-200 text-left">{String(k).replace(/_/g, ' ')}</th>)}
        </tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-teal-50'}>
              {keys.map(k => {
                const v = r[k];
                let s;
                if (typeof v === 'boolean') s = v ? 'YES' : 'no';
                else if (typeof v === 'number') s = fmt(v, k.toLowerCase().includes('p_') ? 6 : 4);
                else s = String(v ?? '-');
                return <td key={k} className="px-2 py-1 border border-teal-200 font-mono">{s}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AnovaTable({ anova }) {
  if (!anova || !anova.anova) return null;
  return <Table rows={anova.anova} cols={['Source', 'DF', 'SS', 'MS', 'F', 'p_value', 'Sig']} />;
}

function StatCard({ label, value, unit }) {
  return (
    <div className="rounded-lg p-3 bg-white border border-teal-200">
      <div className="text-[10px] uppercase text-gray-500">{label}</div>
      <div className="font-mono text-base font-bold" style={{ color: TEAL }}>{value}{unit ? <span className="text-[10px] text-gray-500 ml-1">{unit}</span> : null}</div>
    </div>
  );
}

function ReferencePanel({ cropKey, params }) {
  const ref = PHYSIO_REFERENCE_VALUES[cropKey];
  if (!ref) return null;
  return (
    <Card title={`Published Reference Values (${cropKey})`} testid="ref-values">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {(params || Object.keys(ref)).filter(k => ref[k]).map(k => (
          <div key={k} className="flex justify-between text-xs border-b border-teal-100 py-1">
            <span className="text-gray-600">{k.replace(/_/g, ' ')}</span>
            <span className="font-mono font-semibold" style={{ color: TEAL }}>{ref[k]}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

function Citations({ refs }) {
  return (
    <div className="rounded-xl p-4 mt-4" style={{ background: '#F0FDFA', border: '1px solid #99F6E4' }}>
      <div className="text-xs uppercase tracking-wider font-bold mb-1" style={{ color: TEAL }}>Materials &amp; Methods — References</div>
      <ul className="text-xs space-y-0.5" style={{ color: '#134E4A' }}>
        {(refs || []).map((r, i) => <li key={`citation-${i}-${(r || '').slice(0, 20)}`} dangerouslySetInnerHTML={{ __html: _sanitizeCitation(r) }} />)}
      </ul>
    </div>
  );
}

/* ============================================================
   7. NUTRITIONAL QUALITY
============================================================ */
export function NutritionalQualityView({ data }) {
  const radarData = useMemo(() => {
    if (!data?.nutrient_table || !data?.parameters_detected) return [];
    const params = data.parameters_detected.filter(p => !['Phytic_acid_mg_100g', 'Tannin_mg_100g'].includes(p));
    const top5 = (data.nqi_ranking || []).slice(0, 5).map(r => r.Genotype);
    const maxes = {};
    params.forEach(p => { maxes[p] = Math.max(...data.nutrient_table.map(r => r[p] || 0)); });
    return params.map(p => {
      const row = { Nutrient: p.replace(/_/g, ' ') };
      top5.forEach(g => {
        const gRow = data.nutrient_table.find(r => r.Genotype === g);
        row[g] = gRow ? (100 * (gRow[p] || 0) / (maxes[p] || 1)) : 0;
      });
      return row;
    });
  }, [data]);
  if (!data) return null;
  const top5 = (data.nqi_ranking || []).slice(0, 5).map(r => r.Genotype);
  return (
    <div className="space-y-4" data-testid="nutq-view">
      <p className="text-sm bg-teal-50 rounded-lg p-3 border border-teal-200" data-testid="interpretation">{data.interpretation}</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <StatCard label="Genotypes" value={data.nqi_ranking?.length || 0} />
        <StatCard label="Best biofortified" value={data.best_biofortified_genotype || '-'} />
        <StatCard label="Parameters" value={(data.parameters_detected || []).length} />
        <StatCard label="NQI range" value={`${fmt(data.nqi_ranking?.[data.nqi_ranking.length-1]?.NQI,2)} – ${fmt(data.nqi_ranking?.[0]?.NQI,2)}`} />
      </div>

      <Card title="Nutritional Profile (means)" testid="nut-table"><Table rows={data.nutrient_table} /></Card>

      <Card title="Nutritional Quality Index (NQI) Ranking" testid="nqi-ranking">
        <Table rows={data.nqi_ranking} />
      </Card>

      {data.bioavailability_table?.length > 0 && (
        <Card title="Iron / Zinc Bioavailability — Phytate : Mineral Molar Ratios" testid="bioavail-table">
          <Table rows={data.bioavailability_table} />
          <p className="text-[11px] text-gray-500 mt-2">Phytate:Fe ratio &lt; 1 = high bioavailability; 1–14 = moderate; &gt;14 = low. Phytate:Zn &lt; 5 = high; 5–18 = moderate; &gt;18 = low.</p>
        </Card>
      )}

      {data.rda_comparison_table?.length > 0 && (
        <Card title="ICMR RDA Comparison — % of Adult Daily Allowance" testid="rda-table">
          <Table rows={data.rda_comparison_table} />
        </Card>
      )}

      {top5.length > 0 && radarData.length > 0 && (
        <Card title="Nutrient Profile Radar — Top 5 Genotypes" testid="nut-radar">
          <ResponsiveContainer width="100%" height={360}>
            <RadarChart data={radarData}>
              <PolarGrid />
              <PolarAngleAxis dataKey="Nutrient" fontSize={10} />
              <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} />
              {top5.map((g, i) => <Radar key={g} name={g} dataKey={g} stroke={PALETTE[i % PALETTE.length]} fill={PALETTE[i % PALETTE.length]} fillOpacity={0.2} />)}
              <Legend />
              <Tooltip />
            </RadarChart>
          </ResponsiveContainer>
        </Card>
      )}

      <Card title="ANOVA per parameter" testid="nut-anovas">
        {Object.entries(data.anova_per_parameter || {}).map(([p, a]) => (
          <div key={p} className="mb-3">
            <div className="text-xs font-semibold mb-1" style={{ color: TEAL }}>{p.replace(/_/g, ' ')} — p = {fmt(a.p_value, 6)}</div>
            <AnovaTable anova={a} />
          </div>
        ))}
      </Card>

      <ReferencePanel cropKey="pearl_millet" />
      <Citations refs={[
        'Vasudeva-Menon, V. &amp; Rajeshwari-Devi, M. (2013). Biofortification breeding for mineral density in pearl millet.',
        'ICMR-NIN (2020). Nutrient Requirements and Recommended Dietary Allowances for Indians.',
        'Gibson, R.S. &amp; Hotz, C. (2001). Bioavailability of iron and zinc from plant-based foods.',
      ]} />
    </div>
  );
}

/* ============================================================
   8. PROTEIN & AMINO ACID
============================================================ */
export function ProteinAminoAcidView({ data }) {
  const aaRadarData = useMemo(() => {
    if (!data?.amino_acids_detected || !data?.protein_profile_table) return [];
    const top3 = (data.eaai_table || []).slice(0, 3).map(r => r.Genotype);
    return data.amino_acids_detected.map(aa => {
      const row = { AA: aa };
      top3.forEach(g => {
        const gr = data.protein_profile_table.find(r => r.Genotype === g);
        row[g] = gr ? (gr[aa] || 0) : 0;
      });
      row['FAO_WHO_ref'] = (data.fao_who_reference?.[aa] || 0) / 10;
      return row;
    });
  }, [data]);
  if (!data) return null;
  const top3 = (data.eaai_table || []).slice(0, 3).map(r => r.Genotype);
  return (
    <div className="space-y-4" data-testid="protein-view">
      <p className="text-sm bg-teal-50 rounded-lg p-3 border border-teal-200" data-testid="interpretation">{data.interpretation}</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <StatCard label="Genotypes" value={data.eaai_table?.length || 0} />
        <StatCard label="Best quality (EAAI)" value={data.best_protein_quality_genotype || '-'} />
        <StatCard label="Amino acids" value={(data.amino_acids_detected || []).length} />
        <StatCard label="Clusters found" value={data.cluster_analysis?.n_clusters || '-'} />
      </div>

      <Card title="Protein Profile (fractions + amino acids)" testid="protein-table"><Table rows={data.protein_profile_table} /></Card>

      <Card title="EAAI (Essential Amino Acid Index) — higher is better" testid="eaai-table"><Table rows={data.eaai_table} /></Card>

      <Card title="Chemical Score (limiting amino acid)" testid="chem-score"><Table rows={data.chemical_score_table} /></Card>

      {aaRadarData.length > 0 && top3.length > 0 && (
        <Card title="Amino Acid Radar — Top 3 Genotypes vs FAO/WHO Reference (g/100 g)" testid="aa-radar">
          <ResponsiveContainer width="100%" height={360}>
            <RadarChart data={aaRadarData}>
              <PolarGrid />
              <PolarAngleAxis dataKey="AA" fontSize={11} />
              <PolarRadiusAxis />
              {top3.map((g, i) => <Radar key={g} name={g} dataKey={g} stroke={PALETTE[i % PALETTE.length]} fill={PALETTE[i % PALETTE.length]} fillOpacity={0.2} />)}
              <Radar name="FAO/WHO ref" dataKey="FAO_WHO_ref" stroke="#991B1B" fill="#991B1B" fillOpacity={0.1} strokeDasharray="5 3" />
              <Legend />
              <Tooltip />
            </RadarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {data.cluster_analysis && (
        <Card title={`Ward Hierarchical Cluster — ${data.cluster_analysis.n_clusters} groups by amino-acid profile`} testid="cluster-table">
          <Table rows={data.cluster_analysis.assignments} />
        </Card>
      )}

      <Card title="ANOVA per amino acid" testid="aa-anovas">
        {Object.entries(data.anova_per_aa || {}).map(([aa, a]) => (
          <div key={aa} className="mb-3">
            <div className="text-xs font-semibold mb-1" style={{ color: TEAL }}>{aa} — p = {fmt(a.p_value, 6)}</div>
            <AnovaTable anova={a} />
          </div>
        ))}
      </Card>

      <ReferencePanel cropKey="soybean" />
      <Citations refs={[
        'FAO/WHO (1991). Protein quality evaluation: Report of the Joint FAO/WHO Expert Consultation. Food and Nutrition Paper 51.',
        'Oser, B.L. (1959). An integrated essential amino acid index for predicting the biological value of proteins.',
        'Osborne, T.B. (1907). The proteins of the wheat kernel.',
      ]} />
    </div>
  );
}

/* ============================================================
   9. OIL & FATTY ACID
============================================================ */
export function OilFattyAcidView({ data }) {
  const faRadar = useMemo(() => {
    if (!data?.fatty_acids_detected || !data?.oil_fa_table) return [];
    const top3 = (data.quality_index_ranking || []).slice(0, 3).map(r => r.Genotype);
    return data.fatty_acids_detected.map(fa => {
      const row = { FA: fa.replace(/_/g, ' ') };
      top3.forEach(g => {
        const gr = data.oil_fa_table.find(r => r.Genotype === g);
        row[g] = gr ? (gr[fa + '_percent'] || 0) : 0;
      });
      return row;
    });
  }, [data]);
  if (!data) return null;
  const top3 = (data.quality_index_ranking || []).slice(0, 3).map(r => r.Genotype);
  return (
    <div className="space-y-4" data-testid="oil-view">
      <p className="text-sm bg-teal-50 rounded-lg p-3 border border-teal-200" data-testid="interpretation">{data.interpretation}</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <StatCard label="Genotypes" value={data.oil_fa_table?.length || 0} />
        <StatCard label="Best quality" value={data.best_quality_genotype || '-'} />
        <StatCard label="FSSAI edible" value={(data.fssai_compliance || []).filter(r => r.FSSAI_edible_compliant).length} />
        <StatCard label="Clusters" value={data.cluster_analysis?.n_clusters || '-'} />
      </div>

      <Card title="Oil Content & Fatty Acid Composition" testid="oil-fa-table"><Table rows={data.oil_fa_table} /></Card>

      <Card title="Oil Quality Index Ranking" testid="oil-quality">
        <Table rows={data.quality_index_ranking} />
      </Card>

      {data.fssai_compliance?.length > 0 && (
        <Card title="FSSAI Compliance (edible mustard: erucic < 2%)" testid="fssai-table">
          <Table rows={data.fssai_compliance} />
        </Card>
      )}

      {faRadar.length > 0 && top3.length > 0 && (
        <Card title="Fatty Acid Radar — Top 3 Genotypes" testid="fa-radar">
          <ResponsiveContainer width="100%" height={360}>
            <RadarChart data={faRadar}>
              <PolarGrid />
              <PolarAngleAxis dataKey="FA" fontSize={11} />
              <PolarRadiusAxis />
              {top3.map((g, i) => <Radar key={g} name={g} dataKey={g} stroke={PALETTE[i % PALETTE.length]} fill={PALETTE[i % PALETTE.length]} fillOpacity={0.2} />)}
              <Legend />
              <Tooltip />
            </RadarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {data.cluster_analysis && (
        <Card title={`Ward Cluster by FA Profile — ${data.cluster_analysis.n_clusters} groups`} testid="fa-cluster">
          <Table rows={data.cluster_analysis.assignments} />
        </Card>
      )}

      <Card title="ANOVA" testid="oil-anovas">
        {Object.entries(data.anova_per_parameter || {}).map(([p, a]) => (
          <div key={p} className="mb-3">
            <div className="text-xs font-semibold mb-1" style={{ color: TEAL }}>{p.replace(/_/g, ' ')} — p = {fmt(a.p_value, 6)}</div>
            <AnovaTable anova={a} />
          </div>
        ))}
      </Card>

      <ReferencePanel cropKey="mustard" />
      <Citations refs={[
        'AOAC Official Methods (2016). Fatty acid composition of vegetable oils via GC.',
        'FSSAI (Food Safety and Standards Authority of India) Regulations (2011) — Limits for edible oils.',
        'Velasco, L. &amp; Becker, H.C. (1998). Rapeseed and canola quality analysis.',
      ]} />
    </div>
  );
}

/* ============================================================
   10. ANTIOXIDANT CAPACITY
============================================================ */
export function AntioxidantView({ data }) {
  if (!data) return null;
  return (
    <div className="space-y-4" data-testid="antioxidant-view">
      <p className="text-sm bg-teal-50 rounded-lg p-3 border border-teal-200" data-testid="interpretation">{data.interpretation}</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <StatCard label="Genotypes" value={data.antioxidant_table?.length || 0} />
        <StatCard label="Best antioxidant" value={data.best_antioxidant_genotype || '-'} />
        <StatCard label="Parameters" value={(data.parameters_detected || []).length} />
        <StatCard label="DPPH-Phenolics R²" value={fmt(data.dpph_phenolics_regression?.R2)} />
      </div>

      <Card title="Antioxidant Parameters Table" testid="anti-table"><Table rows={data.antioxidant_table} /></Card>

      <Card title="Composite Antioxidant Index Ranking" testid="comp-index"><Table rows={data.composite_index_ranking} /></Card>

      {data.phenolic_flavonoid_ratio?.length > 0 && (
        <Card title="Phenolic : Flavonoid Ratio" testid="pf-ratio"><Table rows={data.phenolic_flavonoid_ratio} /></Card>
      )}

      {data.dpph_phenolics_regression && (
        <Card title={`DPPH IC50 vs Total Phenolics — R² = ${fmt(data.dpph_phenolics_regression.R2)}, p = ${fmt(data.dpph_phenolics_regression.p_value, 6)}`} testid="dpph-phen-reg">
          <p className="text-xs font-mono text-gray-700">DPPH_IC50 = {data.dpph_phenolics_regression.intercept} + ({data.dpph_phenolics_regression.slope}) × Phenolics (n = {data.dpph_phenolics_regression.n})</p>
          <p className="text-[11px] text-gray-500 mt-1">Negative slope expected — more phenolics → lower IC50 → stronger antioxidant activity.</p>
        </Card>
      )}

      <Card title="Correlation Matrix (antioxidant methods)" testid="anti-corr"><Table rows={data.correlation_matrix} /></Card>

      <Card title="ANOVA" testid="anti-anovas">
        {Object.entries(data.anova_per_parameter || {}).map(([p, a]) => (
          <div key={p} className="mb-3">
            <div className="text-xs font-semibold mb-1" style={{ color: TEAL }}>{p.replace(/_/g, ' ')} — p = {fmt(a.p_value, 6)}</div>
            <AnovaTable anova={a} />
          </div>
        ))}
      </Card>

      <Citations refs={[
        'Brand-Williams, W. et al. (1995). Use of a free radical method to evaluate antioxidant activity. <i>LWT</i> 28: 25–30. (DPPH)',
        'Re, R. et al. (1999). Antioxidant activity applying an improved ABTS radical cation decolorization assay. <i>Free Radic. Biol. Med.</i> 26: 1231–1237.',
        'Benzie, I.F.F. &amp; Strain, J.J. (1996). The Ferric Reducing Ability of Plasma (FRAP). <i>Anal. Biochem.</i> 239: 70–76.',
        'Singleton, V.L. &amp; Rossi, J.A. (1965). Colorimetry of total phenolics with phosphomolybdic–phosphotungstic acid reagents. (Folin-Ciocalteu)',
      ]} />
    </div>
  );
}

/* ============================================================
   11. PLANT HORMONE ANALYSIS
============================================================ */
export function HormoneView({ data }) {
  const trendData = useMemo(() => {
    if (!data?.temporal_trend) return {};
    const byHormone = {};
    data.temporal_trend.forEach(r => {
      byHormone[r.Hormone] = byHormone[r.Hormone] || {};
      byHormone[r.Hormone][r.Time] = byHormone[r.Hormone][r.Time] || { Time: r.Time };
      byHormone[r.Hormone][r.Time][r.Treatment] = r.Concentration;
    });
    const out = {};
    Object.entries(byHormone).forEach(([h, byTime]) => {
      out[h] = Object.values(byTime).sort((a, b) => (a.Time > b.Time ? 1 : -1));
    });
    return out;
  }, [data]);
  if (!data) return null;
  const treatments = [...new Set((data.hormone_table || []).map(r => r.Treatment))];
  return (
    <div className="space-y-4" data-testid="hormone-view">
      <p className="text-sm bg-teal-50 rounded-lg p-3 border border-teal-200" data-testid="interpretation">{data.interpretation}</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <StatCard label="Treatments" value={treatments.length} />
        <StatCard label="Hormones" value={(data.hormones_detected || []).length} />
        <StatCard label="Time points" value={[...new Set((data.temporal_trend || []).map(r => r.Time))].length} />
        <StatCard label="Ratios computed" value={Object.keys((data.ratio_table || [])[0] || {}).filter(k => k.includes('ratio')).length} />
      </div>

      <Card title="Hormone Concentration Means" testid="hormone-table"><Table rows={data.hormone_table} /></Card>

      <Card title="Hormone Ratios (balance indicators)" testid="ratio-table"><Table rows={data.ratio_table} /></Card>

      {Object.entries(trendData).slice(0, 4).map(([hormone, chartData]) => (
        <Card key={hormone} title={`${hormone} — temporal trend`} testid={`trend-${hormone}`}>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="Time" />
              <YAxis label={{ value: `${hormone} (ng/g)`, angle: -90, position: 'insideLeft' }} />
              <Tooltip />
              <Legend />
              {treatments.map((t, i) => <Line key={t} type="monotone" dataKey={t} stroke={PALETTE[i % PALETTE.length]} strokeWidth={2} />)}
            </LineChart>
          </ResponsiveContainer>
        </Card>
      ))}

      <Card title="Correlation Heatmap (hormone × hormone)" testid="hormone-corr"><Table rows={data.correlation_matrix} /></Card>

      <Card title="ANOVA per hormone" testid="hormone-anovas">
        {Object.entries(data.anova_per_hormone || {}).map(([h, a]) => (
          <div key={h} className="mb-3">
            <div className="text-xs font-semibold mb-1" style={{ color: TEAL }}>{h} — p = {fmt(a.p_value, 6)}</div>
            <AnovaTable anova={a} />
          </div>
        ))}
      </Card>

      <Citations refs={[
        'Davies, P.J. (2010). <i>Plant Hormones: Biosynthesis, Signal Transduction, Action!</i> 3rd ed. Springer.',
        'Cutler, S.R. et al. (2010). Abscisic acid: emergence of a core signaling network. <i>Annu. Rev. Plant Biol.</i> 61: 651–679.',
      ]} />
    </div>
  );
}

/* ============================================================
   12. SECONDARY METABOLITES
============================================================ */
export function SecondaryMetabolitesView({ data }) {
  const radarData = useMemo(() => {
    if (!data?.metabolites_detected || !data?.metabolites_table) return [];
    const top4 = (data.anti_nutritional_index || []).slice(0, 4).map(r => r.Genotype);
    const maxes = {};
    data.metabolites_detected.forEach(m => { maxes[m] = Math.max(...data.metabolites_table.map(r => r[m] || 0)); });
    return data.metabolites_detected.map(m => {
      const row = { Metabolite: m };
      top4.forEach(g => {
        const gr = data.metabolites_table.find(r => r.Genotype === g);
        row[g] = gr ? (100 * (gr[m] || 0) / (maxes[m] || 1)) : 0;
      });
      return row;
    });
  }, [data]);
  if (!data) return null;
  const top4 = (data.anti_nutritional_index || []).slice(0, 4).map(r => r.Genotype);
  return (
    <div className="space-y-4" data-testid="secmet-view">
      <p className="text-sm bg-teal-50 rounded-lg p-3 border border-teal-200" data-testid="interpretation">{data.interpretation}</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <StatCard label="Genotypes" value={data.metabolites_table?.length || 0} />
        <StatCard label="Safest (low AN)" value={data.safest_genotype || '-'} />
        <StatCard label="Metabolites" value={(data.metabolites_detected || []).length} />
        <StatCard label="00 GSL lines" value={(data.glucosinolate_class || []).filter(r => r.Class.includes('00')).length} />
      </div>

      <Card title="Secondary Metabolites Table" testid="secmet-table"><Table rows={data.metabolites_table} /></Card>

      {data.anti_nutritional_index?.length > 0 && (
        <Card title="Anti-Nutritional Index (lower is better — safer for food)" testid="antin-idx">
          <Table rows={data.anti_nutritional_index} />
        </Card>
      )}

      {data.glucosinolate_class?.length > 0 && (
        <Card title="Mustard Glucosinolate Classification (00 canola = <30 µmol/g)" testid="gsl-class">
          <Table rows={data.glucosinolate_class} />
        </Card>
      )}

      {radarData.length > 0 && top4.length > 0 && (
        <Card title="Metabolite Profile Radar — 4 Safest Genotypes (% of max)" testid="secmet-radar">
          <ResponsiveContainer width="100%" height={360}>
            <RadarChart data={radarData}>
              <PolarGrid />
              <PolarAngleAxis dataKey="Metabolite" fontSize={10} />
              <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} />
              {top4.map((g, i) => <Radar key={g} name={g} dataKey={g} stroke={PALETTE[i % PALETTE.length]} fill={PALETTE[i % PALETTE.length]} fillOpacity={0.2} />)}
              <Legend />
              <Tooltip />
            </RadarChart>
          </ResponsiveContainer>
        </Card>
      )}

      <Card title="ANOVA per metabolite" testid="secmet-anovas">
        {Object.entries(data.anova_per_metabolite || {}).map(([m, a]) => (
          <div key={m} className="mb-3">
            <div className="text-xs font-semibold mb-1" style={{ color: TEAL }}>{m.replace(/_/g, ' ')} — p = {fmt(a.p_value, 6)}</div>
            <AnovaTable anova={a} />
          </div>
        ))}
      </Card>

      <ReferencePanel cropKey="mustard" />
      <Citations refs={[
        'Fenwick, G.R. et al. (1983). Glucosinolates and their breakdown products in food and food plants. <i>CRC Crit. Rev. Food Sci. Nutr.</i> 18: 123–201.',
        'Reddy, N.R. &amp; Sathe, S.K. (Eds.) (2002). Food Phytates.',
      ]} />
    </div>
  );
}
